import {
  useFetcher,
  Link,
  useLocation,
  useNavigate,
  Form,
} from "@remix-run/react";
import {
  IconApiApp,
  IconArrowUp,
  IconCameraUp,
  IconChevronDown,
  IconCircleMinus,
  IconClipboard,
  IconFileStack,
  IconDeviceDesktop,
  IconMoon,
  IconPasswordFingerprint,
  IconPasswordUser,
  IconSun,
  IconUserCircle,
  IconWorldCog,
} from "@tabler/icons-react";
import { Monitor, ShieldCheck, ShieldAlert } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, Input } from "#app/components/compat/antd";
import { Field, ErrorList, OTPField } from "#app/components/forms.tsx";
import { LocaleSwitcher } from "#app/components/locale-switcher.tsx";
import { Modal } from "#app/components/new-ui/modal";
import { Button } from "#app/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "#app/components/ui/dialog";
import { Icon } from "#app/components/ui/icon";
import { Spinner } from "#app/components/ui/spinner";
import { type loader as keycloakSessionsLoader } from "#app/routes/resources+/keycloak-sessions.tsx";
import {
  useThemePreference,
  persistTheme,
  type ThemePreference,
} from "#app/routes/resources+/theme-switch.tsx";
import { track } from "#app/utils/amplitude.client";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";
import { type KeycloakSessionRow } from "#app/utils/keycloak-session-dto.ts";
import {
  MFA_ENROLL_RETURN_FALLBACK,
  resolveMfaEnrollReturnTo,
  withMfaEnrollQuery,
} from "#app/utils/mfa-enroll.ts";
import { cn, getUserImgSrc, useDoubleCheck } from "#app/utils/misc.tsx";
// The Avatar root has to come from here, not from compat/antd. That one takes a
// `src` and wraps whatever children it is given in an Avatar.Fallback, so
// passing it an AvatarImage nests the image inside the fallback -- which
// renders, loads, hides the fallback, unmounts the image, resets the status and
// starts over. See the comment at the usage below.
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";

type SettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    name: string | null;
    username: string;
    email: string;
    image: { id: string } | null;
    _count: {
      sessions: number;
    };
  };
  hasPassword: boolean;
  isTwoFactorEnabled: boolean;
  isTwoFactorSetupPending?: boolean;
  /** When set, 2FA is managed in Keycloak (enable starts AIA enroll) */
  keycloakId?: string | null;
  /** @deprecated Prefer AIA “Finish setup now”; kept for optional account-console fallback. */
  keycloakAccountUrl?: string | null;
  /** True only when OTP credential is fully configured (not pending setup). */
  hasConfiguredMfa?: boolean;
  /** Identity provider could not be reached for the MFA check. */
  mfaIdpUnavailable?: boolean;
  /** Platform admin (admin/superadmin / ADMIN_EMAILS) — may show required-2FA banner. */
  isPlatformAdmin?: boolean;
  /** False for platform admins — hides/disables the disable-2FA action. */
  canDisableTwoFactor?: boolean;
  /** Shows the "2FA required for admin access" banner (e.g. after /admin/* redirect). */
  mfaRequiredNotice?: boolean;
  onUpdate?: () => void;
  initialTab?: "profile" | "security" | "preferences";
};

type Section = "profile" | "security" | "preferences";

export function SettingsModal({
  open,
  onOpenChange,
  user,
  hasPassword,
  isTwoFactorEnabled,
  isTwoFactorSetupPending = false,
  keycloakId,
  keycloakAccountUrl: _keycloakAccountUrl = null,
  hasConfiguredMfa = false,
  mfaIdpUnavailable = false,
  isPlatformAdmin = false,
  canDisableTwoFactor = true,
  mfaRequiredNotice = false,
  onUpdate,
  initialTab = "profile",
}: SettingsModalProps) {
  const { t } = useTranslation(["nav", "common", "settings"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Section>(initialTab);

  // Fetchers
  const profileFetcher = useFetcher();
  const passwordFetcher = useFetcher();
  const twoFactorEnableFetcher = useFetcher();
  const twoFactorVerifyFetcher = useFetcher();
  const twoFactorDisableFetcher = useFetcher();
  const deleteAccountFetcher = useFetcher();
  const kcSessionsListFetcher = useFetcher<typeof keycloakSessionsLoader>();
  const kcSessionsRevokeFetcher = useFetcher();
  const themePreference = useThemePreference();
  const themeFetcher = useFetcher();

  const handleThemeChange = (nextTheme: ThemePreference) => {
    persistTheme(nextTheme);
    themeFetcher.submit(
      { theme: nextTheme },
      { method: "POST", action: "/resources/theme-switch" },
    );
  };

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processedProfileRef = useRef<string | null>(null);
  const processedPasswordRef = useRef<string | null>(null);
  const processed2FAEnableRef = useRef<string | null>(null);
  const processed2FAVerifyRef = useRef<string | null>(null);
  const processed2FADisableRef = useRef<string | null>(null);

  // State
  const [copied, setCopied] = useState(false);
  const [firstName, setFirstName] = useState(user.name?.split(" ")[0] || "");
  const [lastName, setLastName] = useState(
    user.name?.split(" ").slice(1).join(" ") || "",
  );
  const [newImageSrc, setNewImageSrc] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string[];
    newPassword?: string[];
    confirmNewPassword?: string[];
  }>({});
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [show2FAForm, setShow2FAForm] = useState(false);
  const [show2FAQR, setShow2FAQR] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorErrors, setTwoFactorErrors] = useState<string[]>([]);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [kcRevokeTarget, setKcRevokeTarget] =
    useState<KeycloakSessionRow | null>(null);
  const dc = useDoubleCheck();
  const kcRevokeBusy = kcSessionsRevokeFetcher.state !== "idle";

  // Sync activeTab when initialTab prop changes (when modal opens with different tab)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, open]);

  useEffect(() => {
    if (open && activeTab === "security" && keycloakId) {
      kcSessionsListFetcher.load("/resources/keycloak-sessions");
    }
    // kcSessionsListFetcher identity is stable from useFetcher
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-load when tab/modal/keycloak gating changes
  }, [open, activeTab, keycloakId]);

  const prevKcRevokeState = useRef(kcSessionsRevokeFetcher.state);
  useEffect(() => {
    const prev = prevKcRevokeState.current;
    if (prev !== "idle" && kcSessionsRevokeFetcher.state === "idle") {
      const d = kcSessionsRevokeFetcher.data as {
        success?: boolean;
        error?: string;
        message?: string;
      };
      if (d && typeof d === "object" && "success" in d) {
        if (d.success) {
          toast.success(t("settings:security.sessionRevoked"));
          setKcRevokeTarget(null);
          kcSessionsListFetcher.load("/resources/keycloak-sessions");
        } else {
          toast.error(t("settings:security.sessionsLoadFailed"));
        }
      }
    }
    prevKcRevokeState.current = kcSessionsRevokeFetcher.state;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- kcSessionsListFetcher.load is stable
  }, [kcSessionsRevokeFetcher.state, kcSessionsRevokeFetcher.data, t]);

  // Track when user views the Profile section (modal opened with Profile tab)
  useEffect(() => {
    if (open && initialTab === "profile") {
      track("user:profile:visit", {
        source: "sidebar",
        ...(keycloakId && { keycloakId }),
      });
    }
  }, [open, initialTab, keycloakId]);

  // Reset form states when modal opens
  useEffect(() => {
    if (open) {
      setShowPasswordForm(false);
      setShow2FAForm(false);
      setShow2FAQR(false);
      setQrCode(null);
      setOtpUri(null);
      setTwoFactorCode("");
    }
  }, [open]);

  // Sync local state with user prop changes
  useEffect(() => {
    if (user) {
      setFirstName(user.name?.split(" ")[0] || "");
      setLastName(user.name?.split(" ").slice(1).join(" ") || "");
    }
    setNewImageSrc(null);
  }, [user]);

  // Show delete-account validation error from fetcher
  useEffect(() => {
    const data = deleteAccountFetcher.data as
      | { error?: string; message?: string }
      | undefined;
    if (
      deleteAccountFetcher.state === "idle" &&
      data?.error === "confirmation"
    ) {
      setDeleteError(
        data.message ?? t("settings:profile.invalidDeleteConfirmation"),
      );
    }
  }, [deleteAccountFetcher.state, deleteAccountFetcher.data, t]);

  const copyUserId = () => {
    if (user?.id) {
      void navigator.clipboard.writeText(user.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t("settings:profile.userIdCopied"));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (3MB max)
      if (file.size > 3 * 1024 * 1024) {
        toast.error("Image size must be less than 3MB");
        return;
      }
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(t("settings:profile.invalidImage"));
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewImageSrc(event.target?.result?.toString() ?? null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    // A pending camera pick is only a preview — discard it and keep any
    // saved picture. A second click removes the saved one from the server.
    if (newImageSrc || (fileInputRef.current?.files?.length ?? 0) > 0) {
      setNewImageSrc(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    if (!user?.image?.id) return;

    const formData = new FormData();
    formData.append("intent", "remove-photo");
    profileFetcher.submit(formData, {
      method: "POST",
      action: "/resources/settings-update",
    });
  };

  const handleProfileSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    const formData = new FormData();

    formData.append("intent", "update-profile");
    if (firstName) {
      formData.append("firstName", firstName);
    }
    if (lastName) {
      formData.append("lastName", lastName);
    }
    if (file) {
      formData.append("photoFile", file);
    }

    profileFetcher.submit(formData, {
      method: "POST",
      action: "/resources/settings-update",
      encType: "multipart/form-data",
    });
  };

  const handlePasswordSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("currentPassword", currentPassword);
    formData.append("newPassword", newPassword);
    formData.append("confirmNewPassword", confirmNewPassword);

    passwordFetcher.submit(formData, {
      method: "POST",
      action: "/resources/password-change",
    });
  };

  const handleEnable2FA = () => {
    const formData = new FormData();
    // Pass current path so after Keycloak AIA enrollment we redirect back here
    const returnPath = location.pathname + location.search;
    if (returnPath) formData.append("returnPath", returnPath);
    formData.append("settingsTab", "security");
    twoFactorEnableFetcher.submit(formData, {
      method: "POST",
      action: "/resources/two-factor-enable",
    });
  };

  /** Immediate Keycloak TOTP enrollment (AIA); returns to returnTo or dashboard. */
  const startKeycloakTotpEnrollment = (returnTo?: string | null) => {
    const redirectTo = withMfaEnrollQuery(
      resolveMfaEnrollReturnTo(returnTo ?? MFA_ENROLL_RETURN_FALLBACK),
    );
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/auth/oauth2?kc_action=CONFIGURE_TOTP";
    form.style.display = "none";
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "redirectTo";
    input.value = redirectTo;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  };

  const enrollReturnTo = withMfaEnrollQuery(
    resolveMfaEnrollReturnTo(
      `${location.pathname}${location.search}` || MFA_ENROLL_RETURN_FALLBACK,
    ),
  );

  const handleVerify2FA = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("code", twoFactorCode);

    twoFactorVerifyFetcher.submit(formData, {
      method: "POST",
      action: "/resources/two-factor-verify",
    });
  };

  const handleDisable2FAClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!dc.doubleCheck) {
      e.preventDefault();
      const buttonProps = dc.getButtonProps({});
      buttonProps.onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  // Handle profile update response
  useEffect(() => {
    if (profileFetcher.state === "idle" && profileFetcher.data) {
      const data = profileFetcher.data as {
        success?: boolean;
        message?: string;
        error?: string;
      };
      const responseId = JSON.stringify(data);
      if (processedProfileRef.current === responseId) return;
      processedProfileRef.current = responseId;

      if (data.success) {
        track("user:profile:updated", {
          ...(keycloakId && { keycloakId }),
        });
        toast.success(
          "message" in data
            ? data.message
            : t("settings:profile.updatedSuccess"),
        );
        setNewImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        onUpdate?.();
      } else {
        const errorMessage =
          "error" in data
            ? data.error
            : "Failed to update profile. Please try again.";
        toast.error(
          errorMessage || "Failed to update profile. Please try again.",
        );
      }
    }
  }, [profileFetcher.state, profileFetcher.data, onUpdate, keycloakId, t]);

  // Handle password change response
  useEffect(() => {
    if (passwordFetcher.state === "idle" && passwordFetcher.data) {
      const data = passwordFetcher.data as {
        success?: boolean;
        message?: string;
        error?: string;
        result?: any;
      };
      const responseId = JSON.stringify(data);
      if (processedPasswordRef.current === responseId) return;
      processedPasswordRef.current = responseId;

      if (data.success) {
        toast.success(
          data.message || t("settings:security.passwordChangedSuccess"),
        );
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setPasswordErrors({});
        setShowPasswordForm(false);
        onUpdate?.();
      } else {
        const errorMessage =
          data.error || t("settings:security.passwordChangeFailed");
        toast.error(errorMessage);

        if (data.result?.error) {
          const errors: typeof passwordErrors = {};
          if (
            typeof data.result.error === "object" &&
            data.result.error !== null
          ) {
            const errorObj = data.result.error as Record<string, unknown>;
            Object.keys(errorObj).forEach((key) => {
              const fieldErrors = errorObj[key];
              if (Array.isArray(fieldErrors)) {
                const stringErrors: string[] = [];
                for (const e of fieldErrors) {
                  if (typeof e === "string") {
                    stringErrors.push(e);
                  }
                }
                if (stringErrors.length > 0) {
                  const typedKey = key as keyof typeof errors;
                  errors[typedKey] = stringErrors;
                }
              }
            });
          }
          setPasswordErrors(errors);
        }
      }
    }
  }, [passwordFetcher.state, passwordFetcher.data, onUpdate, t]);

  // Handle 2FA enable response
  useEffect(() => {
    if (
      twoFactorEnableFetcher.state === "idle" &&
      twoFactorEnableFetcher.data
    ) {
      const data = twoFactorEnableFetcher.data as {
        success?: boolean;
        error?: string;
        useKeycloak?: boolean;
        returnTo?: string;
        keycloakAccountUrl?: string;
        config?: unknown;
        message?: string;
      };
      const responseId = JSON.stringify(data);
      if (processed2FAEnableRef.current === responseId) return;
      processed2FAEnableRef.current = responseId;

      if (data.success) {
        if (data.useKeycloak) {
          // Immediate enrollment via Keycloak AIA; OAuth callback returns to returnTo
          // (or dashboard). Account console cannot auto-return after TOTP setup.
          startKeycloakTotpEnrollment(
            data.returnTo?.trim() || MFA_ENROLL_RETURN_FALLBACK,
          );
          return;
        }
        // App-managed: load QR and show verify step
        void fetch("/resources/two-factor-verify")
          .then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then((qrData: unknown) => {
            const typedQrData = qrData as {
              success?: boolean;
              qrCode?: string;
              otpUri?: string;
              error?: string;
            };
            if (
              typedQrData.success &&
              typedQrData.qrCode &&
              typedQrData.otpUri
            ) {
              setQrCode(typedQrData.qrCode);
              setOtpUri(typedQrData.otpUri);
              setShow2FAQR(true);
            } else if (typedQrData.error) {
              toast.error(t("settings:security.qrLoadFailed"));
            }
          })
          .catch((error) => {
            console.error("Failed to load QR code:", error);
            toast.error(t("settings:security.qrLoadFailedRetry"));
          });
      } else {
        toast.error(t("settings:security.enable2FAFailed"));
      }
    }
  }, [
    twoFactorEnableFetcher.state,
    twoFactorEnableFetcher.data,
    onUpdate,
    t,
    activeTab,
  ]);

  // Handle 2FA verify response
  useEffect(() => {
    if (
      twoFactorVerifyFetcher.state === "idle" &&
      twoFactorVerifyFetcher.data
    ) {
      const data = twoFactorVerifyFetcher.data as {
        success?: boolean;
        message?: string;
        error?: string;
        result?: any;
      };
      const responseId = JSON.stringify(data);
      if (processed2FAVerifyRef.current === responseId) return;
      processed2FAVerifyRef.current = responseId;

      if (data.success) {
        toast.success(t("settings:security.twoFactorEnabled"));
        setShow2FAQR(false);
        setQrCode(null);
        setOtpUri(null);
        setTwoFactorCode("");
        setTwoFactorErrors([]);
        setShow2FAForm(false);
        onUpdate?.();
      } else {
        toast.error(t("settings:security.invalidCode"));
        if (data.result?.error?.code) {
          setTwoFactorErrors([data.result.error.code[0]]);
        }
      }
    }
  }, [twoFactorVerifyFetcher.state, twoFactorVerifyFetcher.data, onUpdate, t]);

  // Handle 2FA disable response
  useEffect(() => {
    if (
      twoFactorDisableFetcher.state === "idle" &&
      twoFactorDisableFetcher.data
    ) {
      const data = twoFactorDisableFetcher.data as {
        success?: boolean;
        message?: string;
        error?: string;
        errorCode?: string;
      };
      const responseId = JSON.stringify(data);
      if (processed2FADisableRef.current === responseId) return;
      processed2FADisableRef.current = responseId;

      if (data.success) {
        toast.success(t("settings:security.twoFactorDisabled"));
        setShow2FAForm(false);
        dc.reset();
        onUpdate?.();
      } else if (data.errorCode === "PLATFORM_ADMIN_MFA_REQUIRED") {
        toast.error(t("settings:security.adminCannotDisable2FA"));
      } else {
        toast.error(t("settings:security.disable2FAFailed"));
      }
    }
    // dc.reset is intentionally not in deps – we only want to run when fetcher response changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    twoFactorDisableFetcher.state,
    twoFactorDisableFetcher.data,
    onUpdate,
    t,
  ]);

  const currentImageSrc = newImageSrc ?? getUserImgSrc(user?.image?.id) ?? null;
  const displayInitials = `${firstName.charAt(0)?.toUpperCase() || ""}${lastName.charAt(0)?.toUpperCase() || firstName.charAt(1)?.toUpperCase() || ""}`;

  const mfaNoticeActive =
    mfaRequiredNotice || (isPlatformAdmin && !hasConfiguredMfa);

  // Shared authenticator card on Security. Platform admins see a required banner
  // when 2FA is missing (needed for /admin/* access); they cannot disable 2FA.
  const renderTwoFactorCard = () => {
    const enableLabel = t("settings:security.enable2FA");
    const enableDescription = t("settings:security.twoFactorDescription");
    const enabledStatus = t("settings:security.twoFactorEnabledStatus");
    const activeLabel = t("settings:security.twoFactorActive");
    const enablingLabel = t("settings:security.enabling");
    const disableLabel = t("settings:security.disable2FA");

    return (
      <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
        <h2 className="text-base text-primary font-medium px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
          {t("settings:security.twoFactorTitle")}
        </h2>

        {mfaNoticeActive && (
          <div className="mb-4 p-5 flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300" />
            <div className="space-y-2">
              <div className="text-sm font-medium text-amber-900 dark:text-amber-300">
                {t("settings:security.adminRequiredBannerTitle")}
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                {mfaIdpUnavailable
                  ? t("settings:security.adminIdpUnavailableDescription")
                  : t("settings:security.adminRequiredBannerDescription")}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 p-5">
          <div
            className={`flex items-center justify-between ${!isTwoFactorEnabled && !isTwoFactorSetupPending && !show2FAQR ? "cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors dark:hover:bg-zinc-800" : ""}`}
            onClick={() => {
              if (
                !isTwoFactorEnabled &&
                !isTwoFactorSetupPending &&
                !show2FAQR
              ) {
                setShow2FAForm(!show2FAForm);
              }
            }}
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-950 flex items-center justify-center">
                <Icon
                  name={
                    isTwoFactorSetupPending
                      ? "lock-open-1"
                      : isTwoFactorEnabled
                        ? "lock-closed"
                        : "lock-open-1"
                  }
                  className="h-5 w-5 text-sky-600 dark:text-sky-50"
                />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-zinc-200">
                  {isTwoFactorSetupPending
                    ? t("settings:security.twoFactorPendingStatus")
                    : isTwoFactorEnabled
                      ? enabledStatus
                      : enableLabel}
                </div>
                <div className="text-sm text-gray-500">
                  {isTwoFactorSetupPending
                    ? t("settings:security.twoFactorPendingDescription")
                    : isTwoFactorEnabled
                      ? activeLabel
                      : enableDescription}
                </div>
              </div>
            </div>
          </div>

          {isTwoFactorSetupPending && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-4">
              <Form
                method="POST"
                action="/auth/oauth2?kc_action=CONFIGURE_TOTP"
                // Same-origin /idp redirect: must leave the client router. See
                // ProviderConnectionForm in app/utils/connections.tsx.
                reloadDocument
              >
                <input type="hidden" name="redirectTo" value={enrollReturnTo} />
                <Button
                  type="submit"
                  // className="rounded-lg! px-5! py-2! font-medium!"
                >
                  {t("settings:security.finishSetupNow")}
                </Button>
              </Form>
              <Form
                method="POST"
                action="/logout"
                onSubmit={() => {
                  clearClientAuthStateOnLogout();
                }}
              >
                <Button type="submit">
                  {t("settings:security.signOutToFinishSetup")}
                </Button>
              </Form>
            </div>
          )}

          {show2FAForm &&
            !show2FAQR &&
            !isTwoFactorEnabled &&
            !isTwoFactorSetupPending && (
              <div className="border-t pt-4">
                <Button
                  onClick={handleEnable2FA}
                  disabled={twoFactorEnableFetcher.state !== "idle"}
                  // className="rounded-lg! px-5! py-2! font-medium!"
                >
                  {twoFactorEnableFetcher.state !== "idle"
                    ? enablingLabel
                    : enableLabel}
                </Button>
              </div>
            )}

          {show2FAQR && qrCode && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex flex-col items-center gap-4">
                <img
                  alt={t("settings:security.qrCodeAlt")}
                  src={qrCode}
                  className="h-56 w-56"
                />
                <p className="text-sm text-center">
                  {t("settings:security.scanQrCode")}
                </p>
                {otpUri && (
                  <div className="w-full">
                    <p className="text-xs text-gray-500 mb-2">
                      {t("settings:security.manualCode")}
                    </p>
                    <pre className="whitespace-pre-wrap break-all text-xs p-3 bg-gray-50 rounded border dark:bg-zinc-800">
                      {otpUri}
                    </pre>
                  </div>
                )}
                <p className="text-xs text-gray-500 text-center">
                  {t("settings:security.enterAuthenticatorCode")}
                </p>
                <twoFactorVerifyFetcher.Form
                  onSubmit={handleVerify2FA}
                  className="w-full max-w-xs space-y-4"
                >
                  <OTPField
                    labelProps={{
                      htmlFor: "two-factor-code",
                      children: t("settings:security.code"),
                    }}
                    inputProps={{
                      value: twoFactorCode,
                      onChange: (value: string) => {
                        setTwoFactorCode(value);
                        setTwoFactorErrors([]);
                      },
                      autoFocus: true,
                      autoComplete: "one-time-code",
                    }}
                    errors={twoFactorErrors}
                  />
                  <ErrorList errors={twoFactorErrors} />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={twoFactorVerifyFetcher.state !== "idle"}
                      className="flex-1"
                    >
                      {twoFactorVerifyFetcher.state !== "idle"
                        ? t("settings:security.verifying")
                        : t("settings:security.verify")}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setShow2FAQR(false);
                        setQrCode(null);
                        setOtpUri(null);
                        setTwoFactorCode("");
                        setTwoFactorErrors([]);
                      }}
                    >
                      {t("settings:security.cancel")}
                    </Button>
                  </div>
                </twoFactorVerifyFetcher.Form>
              </div>
            </div>
          )}

          {isTwoFactorEnabled &&
            !isTwoFactorSetupPending &&
            !show2FAQR &&
            canDisableTwoFactor && (
              <twoFactorDisableFetcher.Form
                method="post"
                action="/resources/two-factor-disable"
              >
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={twoFactorDisableFetcher.state !== "idle"}
                  onClick={handleDisable2FAClick}
                  onBlur={() => {
                    const buttonProps = dc.getButtonProps({});
                    if (buttonProps.onBlur) {
                      buttonProps.onBlur(
                        {} as React.FocusEvent<HTMLButtonElement>,
                      );
                    }
                  }}
                  // className="rounded-lg! px-5! py-2! font-medium!"
                >
                  {dc.doubleCheck
                    ? t("settings:security.areYouSure")
                    : disableLabel}
                </Button>
              </twoFactorDisableFetcher.Form>
            )}

          {isTwoFactorEnabled &&
            !isTwoFactorSetupPending &&
            !show2FAQR &&
            !canDisableTwoFactor && (
              <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:bg-zinc-800/60 dark:border-zinc-600">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t("settings:security.adminCannotDisable2FA")}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  const activeTabTitle =
    activeTab === "profile"
      ? t("profile")
      : activeTab === "security"
        ? t("common:securityAndAccess")
        : t("settings:profile.preferences");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] gap-0 overflow-hidden p-0 sm:max-w-[860px]">
        <DialogTitle className="sr-only">
          {t("settings:profile.yourAccount")}
        </DialogTitle>
        <div className="sm:flex sm:h-[600px] max-h-[70dvh] overflow-y-auto">
          {/* Sidebar */}
          <aside className="sm:w-56 border-r border-zinc-100 bg-zinc-100/70 p-5 px-4 dark:border-zinc-800 dark:bg-zinc-900/60">
            <div className="flex items-center gap-2 pb-5">
              {user ? (
                <>
                  {/* {user.image?.id ? (
                  <Avatar src={getUserImgSrc(user.image.id)} size={30} />
                ) : (
                  <Avatar
                    size={30}
                  >
                    {user.username?.charAt(0)?.toUpperCase()}
                  </Avatar>
                )} */}
                  {/* Avatar.Image and Avatar.Fallback must be siblings under
                      the root. This used compat/antd's Avatar, which puts its
                      children inside a Fallback, so the image ended up nested
                      in it: the fallback renders while the status is not
                      "loaded", the image inside loads and sets "loaded", the
                      fallback unmounts, the image unmounts with it and its
                      cleanup resets the status to "idle" -- forever. React
                      caught it as "Maximum update depth exceeded" and the
                      error boundary blanked the page, but only once a user had
                      a picture for the image to load. */}
                  <Avatar className="h-8 w-8 shrink-0">
                    {user.image?.id ? (
                      <AvatarImage
                        src={getUserImgSrc(user.image.id) ?? undefined}
                        alt=""
                      />
                    ) : null}
                    <AvatarFallback className="bg-sky-600 text-white font-medium">
                      {displayInitials ||
                        user.username?.charAt(0)?.toUpperCase() ||
                        "U"}
                    </AvatarFallback>
                  </Avatar>
                </>
              ) : null}

              <div className="min-w-0">
                <p className="font-semibold text-[#353535] dark:text-zinc-200 truncate text-sm">
                  {firstName && lastName
                    ? `${firstName} ${lastName}`
                    : firstName || lastName
                      ? firstName || lastName
                      : user?.id}
                </p>
                <p
                  className="text-xs text-zinc-500 dark:text-zinc-400 truncate"
                  title={user?.email || user?.username}
                >
                  {user?.email || user?.username}
                </p>
              </div>
            </div>

            <nav className="space-y-2 mt-3 hidden sm:block">
              <button
                type="button"
                onClick={() => {
                  if (activeTab !== "profile") {
                    track("user:profile:visit", {
                      source: "settings_modal",
                      ...(keycloakId && { keycloakId }),
                    });
                  }
                  setActiveTab("profile");
                }}
                className={`w-full cursor-pointer text-sm flex items-center gap-2 px-3 py-2 rounded-full transition ${
                  activeTab === "profile"
                    ? "bg-zinc-200 text-zinc-800 font-medium dark:bg-zinc-800 dark:text-zinc-200"
                    : "text-zinc-600 hover:bg-zinc-200/70 dark:text-zinc-400 dark:hover:bg-zinc-800/70"
                }`}
              >
                <IconUserCircle size={18} />
                {t("profile")}
              </button>

              <button
                onClick={() => setActiveTab("security")}
                className={`w-full cursor-pointer text-sm flex items-center gap-2 px-3 py-2 rounded-full transition ${
                  activeTab === "security"
                    ? "bg-zinc-200 text-zinc-800 font-medium dark:bg-zinc-800 dark:text-zinc-200"
                    : "text-zinc-600 hover:bg-zinc-200/70 dark:text-zinc-400 dark:hover:bg-zinc-800/70"
                }`}
              >
                <IconPasswordFingerprint size={18} />
                {t("common:securityAndAccess")}
              </button>

              <Link
                to="/u/api-keys"
                onClick={() => onOpenChange(false)}
                className="w-full cursor-pointer text-sm flex items-center gap-2 px-3 py-2 rounded-full transition text-zinc-600 hover:bg-zinc-200/70 dark:text-zinc-400 dark:hover:bg-zinc-800/70"
              >
                <IconApiApp size={18} />
                {t("apiKeys")}
              </Link>

              <Link
                to="/blog"
                onClick={() => onOpenChange(false)}
                className="w-full cursor-pointer text-sm flex items-center gap-2 px-3 py-2 rounded-full transition text-zinc-600 hover:bg-zinc-200/70 dark:text-zinc-400 dark:hover:bg-zinc-800/70"
              >
                <IconFileStack size={18} />
                {t("nav:changelog")}
              </Link>

              <button
                type="button"
                onClick={() => setActiveTab("preferences")}
                className={`w-full cursor-pointer text-sm flex items-center gap-2 px-3 py-2 rounded-full transition ${
                  activeTab === "preferences"
                    ? "bg-zinc-200 text-zinc-800 font-medium dark:bg-zinc-800 dark:text-zinc-200"
                    : "text-zinc-600 hover:bg-zinc-200/70 dark:text-zinc-400 dark:hover:bg-zinc-800/70"
                }`}
              >
                <IconWorldCog size={18} />
                {t("settings:profile.preferences")}
              </button>
            </nav>

            <div className="mt-3 md:hidden w-full">
              <DropdownMenu>
                {/* `render` rather than a nested <Button>: the trigger renders
                    its own <button>, and <button><button> is invalid HTML that
                    breaks hydration. Same fix as `side-bar.tsx`. */}
                <DropdownMenuTrigger
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 w-full justify-between rounded-full border-zinc-200 bg-zinc-100 px-3 text-sm font-medium shadow-none dark:border-zinc-700 dark:bg-zinc-800"
                    />
                  }
                >
                  <span className="flex items-center gap-2">
                    {activeTab === "profile" && <IconUserCircle size={18} />}
                    {activeTab === "security" && (
                      <IconPasswordFingerprint size={18} />
                    )}
                    {activeTab === "preferences" && <IconWorldCog size={18} />}

                    {activeTabTitle}
                  </span>

                  <IconChevronDown size={16} />
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  className="w-[var(--radix-dropdown-menu-trigger-width)]"
                >
                  <DropdownMenuItem onClick={() => setActiveTab("profile")}>
                    <IconUserCircle size={18} />
                    {t("profile")}
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => setActiveTab("security")}>
                    <IconPasswordFingerprint size={18} />
                    {t("common:securityAndAccess")}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => {
                      onOpenChange(false);
                      navigate("/u/api-keys");
                    }}
                  >
                    <IconApiApp size={18} />
                    {t("apiKeys")}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => {
                      onOpenChange(false);
                      navigate("/blog");
                    }}
                  >
                    <IconFileStack size={18} />
                    {t("nav:changelog")}
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => setActiveTab("preferences")}>
                    <IconWorldCog size={18} />
                    {t("settings:profile.preferences")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 py-4 px-6 overflow-y-auto bg-[#FAFAF9] dark:bg-background">
            <h1 className="text-xl font-medium mb-6 text-foreground">
              {activeTabTitle}
            </h1>

            {/* PROFILE */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                {/* <Card className="shadow-none! rounded-xl border-none !p-0"> */}
                <div className="border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <h3 className="text-base text-primary font-medium mb-4 px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
                    {t("settings:profile.yourAccount")}
                  </h3>

                  <profileFetcher.Form
                    method="POST"
                    action="/resources/settings-update"
                    onSubmit={handleProfileSubmit}
                    encType="multipart/form-data"
                  >
                    <div className="mb-4 w-full space-y-2 px-5 sm:flex sm:items-start sm:gap-6 sm:space-y-0">
                      <div className="flex shrink-0 flex-col items-center gap-2">
                        <div className="relative h-[50px] w-[50px] rounded-full">
                          {currentImageSrc ? (
                            <img
                              src={currentImageSrc}
                              alt={user?.name || user?.username || "User"}
                              className="h-[50px] w-[50px] rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-sky-600 text-2xl font-semibold text-white uppercase">
                              {displayInitials ||
                                user?.username?.charAt(0)?.toUpperCase() ||
                                "U"}
                            </div>
                          )}
                          <button
                            type="button"
                            className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
                            onClick={() => fileInputRef.current?.click()}
                            aria-label={t("settings:profile.changePhoto")}
                            title={t("settings:profile.changePhoto")}
                          >
                            <IconCameraUp
                              size={12}
                              className="text-gray-600 dark:text-gray-400"
                            />
                          </button>
                        </div>
                        {currentImageSrc ? (
                          <button
                            type="button"
                            className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                            onClick={handleRemovePhoto}
                            disabled={profileFetcher.state !== "idle"}
                          >
                            {t("settings:profile.removePhoto")}
                          </button>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1 border-zinc-100 pt-5 sm:border-l sm:pt-0 sm:pl-4 dark:border-zinc-800/30">
                        <label className="mb-2 block font-medium text-[#494949]">
                          {t("settings:profile.email")}
                        </label>
                        <Input
                          value={user?.email || user?.username || ""}
                          disabled
                        />
                      </div>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="photo-upload"
                      name="photoFile"
                    />

                    <Separator className="my-4 bg-zinc-100 dark:bg-zinc-800/30" />

                    <div className="sm:grid grid-cols-2 gap-4 px-5 space-y-2 sm:space-y-0">
                      <div>
                        <label className="text-[#494949] font-medium mb-2 block">
                          {t("settings:profile.firstName")}
                        </label>
                        <Input
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="text-[#494949] font-medium mb-2 block">
                          {t("settings:profile.lastName")}
                        </label>
                        <Input
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="pf-userId-block flex sm:items-center sm:justify-between flex-col sm:flex-row mt-6 bg-[#F5F6FF] dark:bg-zinc-900 p-4 rounded-md rounded-t-none rounded-r-none ">
                      <div className="sm:flex items-center gap-2 text-zinc-600 dark:text-white font-medium space-y-2 sm:space-y-0 mb-2 sm:mb-0">
                        {t("settings:profile.userId")}
                        <span
                          className="bg-white dark:bg-zinc-700 text-sm rounded-full text-zinc-700 dark:text-white inline-flex items-center justify-start gap-1 py-1 px-2 cursor-pointer hover:bg-[#F0F0F0]"
                          onClick={copyUserId}
                        >
                          <IconClipboard
                            size={16}
                            className={
                              copied ? "text-green-700 dark:text-green-300" : ""
                            }
                          />
                          <b className="font-medium truncate max-w-56">
                            {user?.id}
                          </b>
                        </span>
                      </div>
                      <Button
                        type="submit"
                        variant="gradient"
                        disabled={profileFetcher.state !== "idle"}
                      >
                        {profileFetcher.state !== "idle" ? (
                          <Spinner />
                        ) : (
                          <IconArrowUp size={16} />
                        )}
                        {t("settings:profile.updateProfile")}
                      </Button>
                    </div>
                  </profileFetcher.Form>
                </div>
                {/* </Card> */}

                <Card className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <div className="sm:flex items-end justify-between">
                    <div className=" mb-2 sm:mb-0">
                      <h3 className="text-[16px] text-[#141A42] font-medium dark:text-zinc-200">
                        {t("settings:profile.exportTitle")}
                      </h3>
                      <p className="text-sm text-[#AEAEAE] dark:text-zinc-400">
                        {t("settings:profile.exportDesc")}
                      </p>
                    </div>
                    <Link
                      reloadDocument
                      download={`${(user?.email || user?.username)?.replace("@", "-") || user?.id}-data.json`}
                      to="/resources/download-user-data"
                    >
                      <Button
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <IconArrowUp size={16} /> {t("settings:profile.export")}
                      </Button>
                    </Link>
                  </div>
                </Card>

                <Card className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <h3 className="text-base text-destructive font-medium">
                    {t("settings:profile.dangerZone")}
                  </h3>

                  {/* <div className="flex items-center justify-between mb-4 text-[16px]">
                  <span className="text-[#494949]">Leave organization</span>
                  <Button variant="destructive" className="red-button-gradient" onClick={() => {
                      onOpenChange(false);
                      navigate("/settings/organizations");
                    }}>
                    Leave organization
                  </Button>
                </div> */}

                  <div className="sm:flex items-center justify-between text-base space-y-2 sm:space-y-0">
                    <span className="text-[#494949]">
                      {t("settings:profile.accountDeletion")}
                    </span>
                    <Button
                      variant="destructive"
                      // className="red-button-gradient"
                      onClick={() => setDeleteAccountDialogOpen(true)}
                    >
                      <IconCircleMinus /> {t("settings:profile.deleteAccount")}
                    </Button>
                    <Modal
                      title={t("settings:profile.deleteAccountTitle")}
                      open={deleteAccountDialogOpen}
                      onCancel={() => {
                        setDeleteAccountDialogOpen(false);
                        setDeleteConfirmation("");
                        setDeleteError(null);
                      }}
                      footer={null}
                      centered
                      width={480}
                      className="delete-account-modal"
                      destroyOnClose
                      maskClosable={false}
                    >
                      <p className="text-[#494949] mb-4">
                        {t("settings:profile.deleteAccountDesc")}
                      </p>
                      <deleteAccountFetcher.Form
                        method="post"
                        action="/resources/account-delete"
                        onSubmit={() => setDeleteError(null)}
                      >
                        <input
                          type="hidden"
                          name="intent"
                          value="delete-account"
                        />
                        <input
                          type="hidden"
                          name="confirmation"
                          value={deleteConfirmation}
                        />
                        <div className="mb-4">
                          <Input
                            value={deleteConfirmation}
                            onChange={(e) => {
                              setDeleteConfirmation(e.target.value);
                              setDeleteError(null);
                            }}
                            placeholder={t(
                              "settings:profile.deleteConfirmPlaceholder",
                            )}
                            className="w-full"
                            autoComplete="off"
                            disabled={deleteAccountFetcher.state !== "idle"}
                          />
                          {deleteError && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                              {deleteError}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setDeleteAccountDialogOpen(false);
                              setDeleteConfirmation("");
                              setDeleteError(null);
                            }}
                            disabled={deleteAccountFetcher.state !== "idle"}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            type="submit"
                            className="red-button-gradient"
                            disabled={
                              deleteConfirmation.trim() !== "DELETE" ||
                              deleteAccountFetcher.state !== "idle"
                            }
                          >
                            {deleteAccountFetcher.state !== "idle"
                              ? t("settings:profile.deleting")
                              : t("settings:profile.deleteAccount")}
                          </Button>
                        </div>
                      </deleteAccountFetcher.Form>
                    </Modal>
                  </div>
                </Card>
              </div>
            )}

            {/* SECURITY */}
            {activeTab === "security" && (
              <div className="max-w-3xl space-y-6">
                {/* TWO-FACTOR AUTHENTICATION */}
                {renderTwoFactorCard()}

                {/* ACTIVE SSO SESSIONS (Keycloak) */}
                {keycloakId ? (
                  <>
                    <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                      <h2 className="text-base text-primary font-medium px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
                        {t("settings:security.activeSessions")}
                      </h2>
                      <p className="text-sm px-5 text-gray-500 my-3">
                        {t("settings:security.activeSessionsDescription")}
                      </p>
                      {kcSessionsListFetcher.state === "loading" &&
                      !kcSessionsListFetcher.data ? (
                        <p className="text-sm text-gray-500">
                          {t("settings:security.loadingSessions")}
                        </p>
                      ) : null}
                      {kcSessionsListFetcher.data &&
                      "error" in kcSessionsListFetcher.data &&
                      kcSessionsListFetcher.data.error ? (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          {typeof kcSessionsListFetcher.data.error === "string"
                            ? kcSessionsListFetcher.data.error
                            : t("settings:security.sessionsLoadFailed")}
                        </p>
                      ) : null}
                      {kcSessionsListFetcher.data &&
                      "sessions" in kcSessionsListFetcher.data &&
                      kcSessionsListFetcher.data.sessions.length === 0 &&
                      kcSessionsListFetcher.state === "idle" ? (
                        <p className="text-sm text-gray-500">
                          {t("settings:security.noActiveSessions")}
                        </p>
                      ) : null}
                      {kcSessionsListFetcher.data &&
                      "sessions" in kcSessionsListFetcher.data &&
                      kcSessionsListFetcher.data.sessions.length > 0 ? (
                        <div className="space-y-1 border-t border-gray-100 dark:border-zinc-800/30 pt-1">
                          {kcSessionsListFetcher.data.sessions.map((row) => (
                            <div
                              key={row.id}
                              className="flex flex-col gap-2 bg-gray-50/80 dark:bg-zinc-800/30 p-4 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="flex gap-3 min-w-0">
                                <Monitor className="h-5 w-5 shrink-0 text-gray-500 mt-0.5" />
                                <div className="min-w-0 text-sm">
                                  <div className="font-medium text-gray-900 dark:text-zinc-200">
                                    IP {row.ipAddress ?? "—"}
                                  </div>
                                  <div className="text-gray-600 mt-1 space-y-0.5 dark:text-gray-400">
                                    <div>
                                      {t("settings:security.started")}:{" "}
                                      {row.startedAt
                                        ? new Date(
                                            row.startedAt,
                                          ).toLocaleString()
                                        : "—"}
                                    </div>
                                    <div>
                                      {t("settings:security.lastAccess")}:{" "}
                                      {row.lastAccessAt
                                        ? new Date(
                                            row.lastAccessAt,
                                          ).toLocaleString()
                                        : "—"}
                                    </div>
                                    {/* <div
                                    className="truncate"
                                    title={row.clientsLabel}
                                  >
                                    Clients: {row.clientsLabel}
                                  </div> */}
                                    {row.username ? (
                                      <div>
                                        {t("settings:security.user")}:{" "}
                                        {row.username}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="shrink-0 self-start sm:self-center"
                                disabled={kcRevokeBusy}
                                onClick={() => setKcRevokeTarget(row)}
                              >
                                {t("settings:security.revoke")}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <Modal
                      title={t("settings:security.revokeSessionTitle")}
                      open={kcRevokeTarget !== null}
                      closable={!kcRevokeBusy}
                      maskClosable={!kcRevokeBusy}
                      onCancel={() => {
                        if (!kcRevokeBusy) setKcRevokeTarget(null);
                      }}
                      footer={
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="destructive"
                            disabled={kcRevokeBusy}
                            onClick={() => setKcRevokeTarget(null)}
                          >
                            {t("settings:security.cancel")}
                          </Button>
                          <Button
                            disabled={kcRevokeBusy || !kcRevokeTarget}
                            onClick={() => {
                              if (!kcRevokeTarget || kcRevokeBusy) return;
                              const fd = new FormData();
                              fd.set("intent", "revokeSession");
                              fd.set("sessionId", kcRevokeTarget.id);
                              kcSessionsRevokeFetcher.submit(fd, {
                                method: "post",
                                action: "/resources/keycloak-sessions",
                              });
                            }}
                          >
                            {kcRevokeBusy ? <Spinner /> : null}
                            {t("settings:security.revokeSession")}
                          </Button>
                        </div>
                      }
                    >
                      {kcRevokeTarget ? (
                        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                          <p>
                            {t("settings:security.revokeSessionDescription")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
                            <li>
                              {t("settings:security.ip")}:{" "}
                              {kcRevokeTarget.ipAddress ?? "—"}
                            </li>
                            <li>
                              {t("settings:security.started")}:{" "}
                              {kcRevokeTarget.startedAt
                                ? new Date(
                                    kcRevokeTarget.startedAt,
                                  ).toLocaleString()
                                : "—"}
                            </li>
                            <li>
                              {t("settings:security.lastAccess")}:{" "}
                              {kcRevokeTarget.lastAccessAt
                                ? new Date(
                                    kcRevokeTarget.lastAccessAt,
                                  ).toLocaleString()
                                : "—"}
                            </li>
                            {/* <li>Clients: {kcRevokeTarget.clientsLabel}</li> */}
                          </ul>
                        </div>
                      ) : null}
                    </Modal>
                  </>
                ) : null}

                {/* PASSWORD */}
                <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <h2 className="text-base text-primary font-medium px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
                    {t("settings:security.password")}
                  </h2>

                  <div
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 p-3 transition-colors mb-4"
                    onClick={() => setShowPasswordForm(!showPasswordForm)}
                  >
                    <div className="flex items-center gap-3 flex-1 px-2">
                      <IconPasswordUser />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-zinc-200">
                          {hasPassword
                            ? t("settings:security.changePassword")
                            : t("settings:security.createPassword")}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-zinc-400">
                          {hasPassword
                            ? t("settings:security.updatePasswordDesc")
                            : t("settings:security.setupPasswordDesc")}
                        </div>
                      </div>
                    </div>
                    {/* <Button className="rounded-lg! px-5! py-2! font-medium!" onClick={(e) => {
                      e.stopPropagation();
                      setShowPasswordForm(!showPasswordForm);
                    }}>
                    {hasPassword
                      ? t("settings:security.changePassword")
                      : t("settings:security.createPassword")}
                  </Button> */}
                  </div>

                  {showPasswordForm && (
                    <passwordFetcher.Form
                      onSubmit={handlePasswordSubmit}
                      className="space-y-1 border-t border-zinc-100 dark:border-zinc-800/30 px-5 py-5"
                    >
                      {hasPassword && (
                        <Field
                          labelProps={{
                            children: t("settings:security.currentPassword"),
                          }}
                          inputProps={{
                            type: "password",
                            value: currentPassword,
                            onChange: (e) => {
                              setCurrentPassword(e.target.value);
                              if (passwordErrors.currentPassword) {
                                setPasswordErrors((prev) => ({
                                  ...prev,
                                  currentPassword: undefined,
                                }));
                              }
                            },
                            autoComplete: "current-password",
                          }}
                          errors={passwordErrors.currentPassword}
                        />
                      )}
                      <Field
                        labelProps={{
                          children: t("settings:security.newPassword"),
                        }}
                        inputProps={{
                          type: "password",
                          value: newPassword,
                          onChange: (e) => {
                            setNewPassword(e.target.value);
                            if (passwordErrors.newPassword) {
                              setPasswordErrors((prev) => ({
                                ...prev,
                                newPassword: undefined,
                              }));
                            }
                          },
                          autoComplete: "new-password",
                        }}
                        errors={passwordErrors.newPassword}
                      />
                      <Field
                        labelProps={{
                          children: t("settings:security.confirmNewPassword"),
                        }}
                        inputProps={{
                          type: "password",
                          value: confirmNewPassword,
                          onChange: (e) => {
                            setConfirmNewPassword(e.target.value);
                            if (passwordErrors.confirmNewPassword) {
                              setPasswordErrors((prev) => ({
                                ...prev,
                                confirmNewPassword: undefined,
                              }));
                            }
                          },
                          autoComplete: "new-password",
                        }}
                        errors={passwordErrors.confirmNewPassword}
                      />
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          disabled={passwordFetcher.state !== "idle"}
                          variant="gradient"
                          // className="rounded-lg! px-5! py-2! font-medium!"
                        >
                          {passwordFetcher.state !== "idle"
                            ? t("settings:security.updating")
                            : hasPassword
                              ? t("settings:security.updatePassword")
                              : t("settings:security.createPasswordAction")}
                        </Button>
                      </div>
                    </passwordFetcher.Form>
                  )}
                </div>

                {/* SOCIAL LOGINS */}
                {/* <div className="rounded-xl bg-white p-6 shadow-sm">
<h2 className="text-lg font-semibold text-gray-900">
  Social logins
</h2>
<p className="mt-1 text-sm text-gray-500">
  Manage your logins through Google or Microsoft.
</p>

<Divider className="my-4" />

<div className="space-y-4">
  
  <div className="flex items-center justify-between">
    <span className="text-base font-medium text-gray-900">
      Google
    </span>

    <Button disabled className="rounded-[10px]! px-4!"><img
          src="/img/new-icons/google-logo.svg"
          alt="Google"
        />
      Account linked
    </Button>
  </div>

  <div className="flex items-center justify-between">
    <span className="text-base font-medium text-gray-900">
      Microsoft
    </span>

    <Button className="rounded-[10px]! px-4!"><img
          src="/img/new-icons/microsoft.svg"
          alt="Microsoft"
        />
      Link account
    </Button>
  </div>

  
  <div className="flex items-center justify-between">
    <span className="text-base font-medium text-gray-900">
      Apple
    </span>

    <Button className="rounded-[10px]! px-4!"><img src="/img/new-icons/apple.svg" alt="Apple" />
      Link account
    </Button>
  </div>
</div>
</div>   */}

                {/* ORGANIZATIONS */}
                <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <div
                    onClick={() => {
                      onOpenChange(false);
                      navigate("/settings/organizations");
                    }}
                    className="flex items-center gap-3 p-5 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer"
                  >
                    <Icon name="users" className="h-5 w-5 dark:text-zinc-200" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-zinc-200">
                        {t("settings:organizations.title")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-zinc-400">
                        {t("settings:organizations.subtitle")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PREFERENCES */}
            {activeTab === "preferences" && (
              <div className="max-w-3xl space-y-6">
                <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <h3 className="text-base text-primary font-medium px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
                    {t("settings:profile.appearance")}
                  </h3>
                  <div className="p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t("settings:profile.appearanceDesc")}
                      </p>
                    </div>
                    <div
                      className="inline-flex shrink-0 items-center rounded-full border border-zinc-200 bg-zinc-50 p-1 dark:border-zinc-700 dark:bg-zinc-900"
                      role="group"
                      aria-label={t("settings:profile.themeToggle")}
                    >
                      {[
                        {
                          value: "system" as const,
                          label: t("settings:profile.systemMode"),
                          icon: IconDeviceDesktop,
                        },
                        {
                          value: "light" as const,
                          label: t("settings:profile.lightMode"),
                          icon: IconSun,
                        },
                        {
                          value: "dark" as const,
                          label: t("settings:profile.darkMode"),
                          icon: IconMoon,
                        },
                      ].map(({ value, label, icon: ThemeIcon }) => {
                        const selected = themePreference === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => handleThemeChange(value)}
                            className={cn(
                              "flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-zinc-500 transition-all hover:text-foreground",
                              selected &&
                                "bg-white text-foreground shadow-xs dark:bg-zinc-800 dark:text-zinc-100",
                            )}
                          >
                            <ThemeIcon
                              className={cn(
                                "size-4",
                                value === "light" &&
                                  selected &&
                                  "text-amber-500 dark:text-amber-300",
                                value === "dark" &&
                                  selected &&
                                  "text-blue-500 dark:text-blue-300",
                              )}
                            />
                            {/* Icon-only on phones, but still announced: `hidden` would leave
                                the button with no accessible name. */}
                            <span className="sr-only sm:not-sr-only">
                              {label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="shadow-none! ring-0! border border-zinc-200/80 bg-white rounded-2xl overflow-hidden dark:bg-card dark:border-border">
                  <h3 className="text-base text-primary font-medium px-4 bg-zinc-100 py-4 dark:bg-zinc-800/60 dark:text-zinc-100">
                    {t("settings:profile.language")}
                  </h3>
                  <div className="p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t("settings:profile.languageDesc")}
                      </p>
                    </div>
                    <LocaleSwitcher variant="settings" />
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
